import React from 'react';
import { Clock, Mail } from 'lucide-react';

/**
 * Wartungsseite für Besucher.
 *
 * Props:
 * - settings: { maintenance_title, maintenance_message, show_contact_email, contact_email }
 * - embedded: wenn true, rendert ohne `fixed inset-0` (für Admin-Vorschau).
 */
const MaintenanceMode = ({ settings, embedded = false }) => {
  const title = settings?.maintenance_title || 'Wir arbeiten gerade an etwas Neuem!';
  const message =
    settings?.maintenance_message ||
    'Unser Shop wird gerade überarbeitet. Wir sind bald wieder für Sie da.';
  const showEmail = settings?.show_contact_email;
  const email = settings?.contact_email || '';

  const wrapperClasses = embedded
    ? 'w-full flex-1 bg-gradient-to-br from-[#F5EDE4] to-[#E8DED1] flex items-center justify-center p-6 md:p-10'
    : 'fixed inset-0 z-[9999] bg-gradient-to-br from-[#F5EDE4] to-[#E8DED1] flex items-center justify-center p-4';

  return (
    <div className={wrapperClasses} data-testid={embedded ? 'maintenance-preview' : 'maintenance-page'}>
      <div className="max-w-lg w-full text-center">
        {/* Farbiges Homepage-Logo */}
        <div className="mb-6 flex flex-col items-center">
          <img
            src="/navbar-logo.png?v=2026032611"
            alt="Homeyoursoul"
            className="h-20 md:h-24 w-auto mb-3"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(93,78,61,0.15))' }}
          />
          <span className="text-lg tracking-wider text-[#5D4E3C] font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
            Homeyoursoul
          </span>
          <span className="text-xs text-[#8B7355] mt-1">Lieblingsstücke für dich und dein Zuhause</span>
        </div>

        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center shadow-lg">
            <Clock className="h-10 w-10 md:h-12 md:w-12 text-[#8B7355]" />
          </div>
        </div>

        {/* Titel + Nachricht */}
        <h2 className="text-2xl md:text-3xl font-bold text-[#5D4E3C] mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          {title}
        </h2>
        <p className="text-sm md:text-base text-[#6B5B4A] mb-6 leading-relaxed whitespace-pre-line">
          {message}
        </p>

        {/* Kontakt */}
        {showEmail && email && (
          <a
            href={`mailto:${email}`}
            className="inline-flex items-center gap-2 text-sm md:text-base text-[#8B7355] hover:text-[#5D4E3C] underline"
          >
            <Mail className="h-4 w-4" />
            {email}
          </a>
        )}

        <div className="mt-8 pt-6 border-t border-[#D4C5B0]">
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 mb-3 text-xs" data-testid="maintenance-legal-links">
            <a href="/impressum" className="text-[#8B7355] hover:text-[#5D4E3C] underline">Impressum</a>
            <a href="/datenschutz" className="text-[#8B7355] hover:text-[#5D4E3C] underline">Datenschutz</a>
            <a href="/agb" className="text-[#8B7355] hover:text-[#5D4E3C] underline">AGB</a>
            <a href="/widerruf" className="text-[#8B7355] hover:text-[#5D4E3C] underline">Widerruf</a>
          </nav>
          <p className="text-xs text-[#8B7355]">© {new Date().getFullYear()} Homeyoursoul · homeyoursoul.com</p>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMode;
